import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { parseEnabledFlag, isMedicalRecordsEnabled } from '../../../../../utils/authConfig';
import {
  useCreateMedicalRecordsDemoMutation, usePrepareMedicalRecordsDemoMutation,
  useGetMedicalRecordsDocumentsQuery, useDownloadMedicalRecordsDocumentMutation,
} from '../data';
import type { MedicalRecordsDocumentScope, MedicalRecordsDocumentView } from '../data/medicalRecordsViews';

export const isMedicalRecordsDemoEnabled = (): boolean => isMedicalRecordsEnabled()
  && parseEnabledFlag(process.env.REACT_APP_MEDICAL_RECORDS_DEMO_ENABLED);

export function useSyntheticMedicalRecordsRequest(company: string, centerId: string) {
  const [create, state] = useCreateMedicalRecordsDemoMutation();
  const [error, setError] = useState(false);
  const key = useRef('');
  const active = useRef(true);
  const navigate = useNavigate();
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);

  const submit = async () => {
    setError(false);
    try {
      key.current ||= crypto.randomUUID();
      await create({ company, centerId, idempotencyKey: key.current }).unwrap();
      if (active.current) navigate(`/medical-records/requests?${new URLSearchParams({ company, center: centerId })}`);
    } catch {
      if (active.current) setError(true);
    }
  };
  return { submit, isLoading: state.isLoading, error };
}

export function useSyntheticMedicalRecordsPreparation(scope: MedicalRecordsDocumentScope) {
  const [prepare, state] = usePrepareMedicalRecordsDemoMutation();
  const [error, setError] = useState(false);
  const submit = async () => {
    setError(false);
    try { await prepare(scope).unwrap(); }
    catch { setError(true); }
  };
  return { submit, isLoading: state.isLoading, error };
}

export function useMedicalRecordsDocuments(scope: MedicalRecordsDocumentScope) {
  const query = useGetMedicalRecordsDocumentsQuery(scope, { refetchOnMountOrArgChange: true });
  const [download, state] = useDownloadMedicalRecordsDocumentMutation();
  const [downloadError, setDownloadError] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const save = async (document?: MedicalRecordsDocumentView) => {
    setDownloadError(false);
    setDownloaded(false);
    try {
      await download({ ...scope, documentId: document?.documentId,
        fileName: document?.fileName ?? 'Medical records.zip' }).unwrap();
      setDownloaded(true);
    } catch { setDownloadError(true); }
  };
  return { query, save, isDownloading: state.isLoading, downloadError, downloaded };
}
